import Header from "./Layout/Header";
import LeftSideBar from "./Layout/LeftSideBar";
import { NavLink } from "react-router-dom";
import { trans, showError, showSuccess, showErrorModal } from "./utils";
import React, { useState, useEffect, Suspense } from 'react';
import axios from "axios";
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import ErrorModal from './Common/ErrorModal';
import { useSelector } from 'react-redux'
import config from '../config.js';
import ChatFeature from './Chat';

// Create a map of all possible component modules in the current directory and subdirectories
// This allows Vite to bundling them correctly instead of ignoring dynamic imports
const modules = import.meta.glob('./**/*.{jsx,js}');

const Template = (data) => {
    const SystemUrl = config.SystemUrl;
    const minimize = useSelector((state) => state.menuToggle.minimize)

    //Render Component - useMemo to recreate lazy component when path changes
    const ComponentToRender = React.useMemo(() => {
        const path = `./${data.getfolder}/${data.getaction}`;
        // Try to find the module with .jsx or .js extension
        const importer = modules[`${path}.jsx`] || modules[`${path}.js`];

        if (!importer) {
            console.error(`[Template] Module not found: ${path}`);
            // Return a dummy component that throws an error to be caught by Suspense/Boundary or just logs
            return React.lazy(() => Promise.reject(new Error(`Module not found: ${path}`)));
        }

        return React.lazy(importer);
    }, [data.getfolder, data.getaction]);
    //Get Module Variables FROM API
    let GetModuleData = JSON.parse(localStorage.getItem(data.getmodule)) || data.getmodule;
    let module_name = data.getmodule;
    if (GetModuleData && GetModuleData.role_id != 0) {
        module_name = GetModuleData.module_name
    }
    const [file, setFile] = useState(null);

    const [isDisabled, setDisabled] = useState(false);
    const [isSidebarHovered, setIsSidebarHovered] = useState(false);

    const UploadExcel = () => {
        if (!file) {
            showError('Please select a file to upload');
            return;
        }
        setDisabled(true);
        let formData = new FormData();
        formData.append("file", file);
        //general excel import
        axios.post(SystemUrl + data.getmodule + '/import', formData, {
            headers: {
                "Content-Type": "multipart/form-data",
            },
        }).then(res => {
            // Trigger a custom event that DataTable components can listen to
            window.dispatchEvent(new CustomEvent('datatableReload', { detail: { tableId: 'datatable' } }));
            showSuccess('Success');
            setDisabled(false);
            setFile(null);

            // Close the modal
            const modalEl = document.getElementById('AssignModal');
            if (modalEl) {
                if (typeof bootstrap !== 'undefined' && bootstrap.Modal) {
                    const modalInstance = bootstrap.Modal.getInstance(modalEl);
                    if (modalInstance) {
                        modalInstance.hide();
                    } else {
                        const closeBtn = modalEl.querySelector('[data-bs-dismiss="modal"]');
                        if (closeBtn) {
                            closeBtn.click();
                        } else {
                            new bootstrap.Modal(modalEl).hide();
                        }
                    }
                } else {
                    const closeBtn = modalEl.querySelector('[data-bs-dismiss="modal"]');
                    if (closeBtn) {
                        closeBtn.click();
                    }
                }

                // Reset file input value
                const fileInput = modalEl.querySelector('input[type="file"]');
                if (fileInput) {
                    fileInput.value = '';
                }
            }
        }).catch((err) => {
            setDisabled(false);
            showErrorModal(err, trans('Upload Errors'));
        });
    }
    //Dashboard Has no Variables
    if (!GetModuleData) {
        GetModuleData = {}
        GetModuleData.name_en = 'Dashboard';
    }
    let SystemTitle = localStorage.getItem('SystemTitle') ? localStorage.getItem('SystemTitle') : "HIC - AEON";
    let PageModuleTitle = trans(GetModuleData.name_en) ? trans(GetModuleData.name_en) : "Dashboard";

    const folder = data.getfolder

    // Cancel all in-flight requests from the previous page
    useEffect(() => {
        if (window.cancelNavigationRequests) {
            window.cancelNavigationRequests();
        }
    }, [data.getmodule, data.getaction, data.getfolder]);

    useEffect(() => {
        document.title = SystemTitle + " | " + PageModuleTitle + " - " + data.getaction

        let delegationInstance = null;

        const cleanupTooltips = () => {
            document.querySelectorAll('.tooltip').forEach(el => el.remove());
        };

        const handleClick = (e) => {
            if (e.target.closest('[data-bs-toggle="tooltip"]')) {
                cleanupTooltips();
            }
        };

        const handleMouseOver = (e) => {
            // Force-clear any orphaned stuck tooltips if the mouse is no longer over a trigger.
            // This catches elements that were destroyed by React (e.g. data load) while being hovered.
            if (!e.target.closest('[data-bs-toggle="tooltip"]') && !e.target.closest('.tooltip')) {
                cleanupTooltips();
            } else {
                // Always enforce a maximum of 1 tooltip on screen at a time to prevent duplicates
                const tooltips = document.querySelectorAll('.tooltip');
                if (tooltips.length > 1) {
                    for (let i = 0; i < tooltips.length - 1; i++) {
                        tooltips[i].remove();
                    }
                }
            }
        };

        // Initialize Bootstrap 5 tooltips via delegation
        try {
            if (typeof bootstrap !== 'undefined' && bootstrap.Tooltip) {
                // Dispose all old individual tooltips first to avoid conflicts
                document.querySelectorAll('[data-bs-toggle="tooltip"]').forEach(el => {
                    const instance = bootstrap.Tooltip.getInstance(el);
                    if (instance) instance.dispose();
                });

                // Clear stray tooltips from DOM
                cleanupTooltips();

                // Use DOM delegation for ALL tooltips. This attaches listeners to the body, 
                // preventing tooltips from getting stuck when React unmounts or re-renders the element.
                delegationInstance = new bootstrap.Tooltip(document.body, {
                    selector: '[data-bs-toggle="tooltip"]',
                    trigger: 'hover'
                });

                document.body.addEventListener('click', handleClick);
                document.addEventListener('mouseover', handleMouseOver);
            }
        } catch (e) {
            console.warn('[Template] Tooltip initialization failed:', e);
        }

        return () => {
            document.body.removeEventListener('click', handleClick);
            document.removeEventListener('mouseover', handleMouseOver);
            if (delegationInstance) {
                delegationInstance.dispose();
            }
            cleanupTooltips();
        };
    }, [SystemTitle, PageModuleTitle, data.getaction])
    return (

        <div id="kt_body" data-kt-aside-minimize={isSidebarHovered ? 'off' : minimize} className="header-fixed header-tablet-and-mobile-fixed toolbar-enabled toolbar-fixed toolbar-tablet-and-mobile-fixed aside-enabled aside-fixed">
            {folder != "FormBuilder" ? (
                <div className="header-fixed header-tablet-and-mobile-fixed toolbar-enabled toolbar-fixed toolbar-tablet-and-mobile-fixed aside-enabled aside-fixed">
                    <div className="d-flex flex-column flex-root">
                        <div className="page d-flex flex-row flex-column-fluid">
                            <LeftSideBar onHover={setIsSidebarHovered} />
                            <div className="wrapper d-flex flex-column flex-row-fluid" id="kt_wrapper">
                                <Header />
                                <div className="content d-flex flex-column flex-column-fluid" id="kt_content">
                                    <div className="toolbar" id="kt_toolbar">

                                        <div id="kt_toolbar_container" className="container-fluid d-flex flex-stack">

                                            <div data-kt-place="true" data-kt-place-mode="prepend"
                                                data-kt-place-parent="{default: '#kt_content_container', 'lg': '#kt_toolbar_container'}"
                                                className="page-title d-flex align-items-center me-3 flex-wrap mb-2 mb-lg-0 lh-1">

                                                <h1 className="d-none align-items-center text-dark fw-bolder my-1 fs-3">{trans(GetModuleData.name_en)}</h1>

                                                <span className="d-none h-20px border-gray-200 border-start mx-4"></span>

                                                <ul className="breadcrumb breadcrumb-separatorless fw-bold fs-7 my-1">

                                                    <li className="breadcrumb-item text-muted">
                                                        <NavLink to='/dashboard'
                                                            className="text-muted text-hover-primary">{trans('Dashboard')}</NavLink>
                                                    </li>

                                                    <li className="breadcrumb-item text-dark">{trans(GetModuleData.name_en)}</li>

                                                </ul>

                                            </div>

                                            {!!GetModuleData.add && !!GetModuleData.page_add && data.getaction != 'Add' && !(localStorage.getItem('userRole') == 34 && GetModuleData.name_en == "MyAsset") ?

                                                <div className="d-flex align-items-center gap-3 py-1 toolbar-actions">

                                                    <div>
                                                        <NavLink to={`/${data.getmodule}/Add`}
                                                            className="btn btn-light-primary font-weight-bold toolbar-btn"
                                                            data-kt-menu-trigger="click" data-kt-menu-placement="bottom-end"
                                                            data-kt-menu-flip="top-end"
                                                            data-container="body" data-bs-toggle="tooltip" data-placement="bottom" data-bs-title={trans(GetModuleData.name_en)}
                                                        >
                                                            <i className="bi bi-plus fs-2"></i> {trans('Create')}
                                                        </NavLink>
                                                    </div>

                                                    <div data-container="body" data-bs-toggle="tooltip" data-placement="bottom" data-bs-title={trans(GetModuleData.name_en)}>
                                                        <button type="button" className="btn btn-light-primary font-weight-bold toolbar-btn" data-bs-toggle="modal" data-bs-target="#AssignModal" >
                                                            <i className="bi bi-cloud-upload fs-2"></i> {trans('Upload')}
                                                        </button>
                                                    </div>

                                                </div>
                                                : <br />
                                            }
                                        </div>

                                    </div>

                                    <div className="modal fade" id="AssignModal" tabIndex="-1" role="dialog"
                                        aria-labelledby="AssignModalLabel" aria-hidden="true">
                                        <div className="modal-dialog" role="document">
                                            <div className="modal-content">
                                                <div className="modal-header">
                                                    <h5 className="modal-title" id="exampleModalLabel">{trans('Upload')} {trans(GetModuleData.name_en)} <a href={SystemUrl + data.getmodule + '?export=1'} download>{trans('Download Sample')}</a></h5>
                                                    <button type="button" className="btn-close" data-bs-dismiss="modal" aria-label="Close"></button>
                                                </div>
                                                <div className="modal-body">
                                                    <form>

                                                        <div className="form-group">
                                                            <label htmlFor="message-text"
                                                                className="col-form-label">{trans('Actions options')} :</label>
                                                            <input onChange={e => setFile(e.target.files[0])} type="file" className="form-control" required />
                                                        </div>
                                                        <div className="modal-footer">
                                                            <div className="row">

                                                                <div className="col-md-6">
                                                                    <button type="button" className="btn btn-secondary" data-bs-dismiss="modal">{trans('Close')}</button>
                                                                </div>
                                                                <div className="col-md-6">
                                                                    <button type="button" onClick={UploadExcel} className="btn btn-primary" disabled={isDisabled}>{trans('Submit')}</button>

                                                                </div>


                                                            </div>

                                                        </div>
                                                    </form>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                    <div className="post d-flex flex-column-fluid" id="kt_post">
                                        <div className="container" id="kt_content_container">
                                            <Suspense fallback={<div className="p-5 text-center">Loading...</div>}>
                                                <ComponentToRender RoleId={GetModuleData.role_id} CurrentModule={data.getmodule} SystemUrl={SystemUrl} />
                                            </Suspense>
                                        </div>
                                    </div>

                                </div>

                            </div>
                        </div>
                    </div>
                </div>) :
                <Suspense fallback={<div className="p-5 text-center">Loading...</div>}>
                    <ComponentToRender CurrentModule={data.getmodule} SystemUrl={SystemUrl} />
                </Suspense>
            }
            <ToastContainer
                position="top-center"
                autoClose={false}
                closeButton={true}
                rtl={localStorage.getItem('lang') === 'ar'}
                theme="light"
            />
            <ErrorModal />
            <ChatFeature
                currentDataContext={{
                    viewType: 'table',
                    filters: {},
                    selectedAssets: [],
                    description: trans(GetModuleData?.name_en || GetModuleData?.name || (typeof GetModuleData === 'string' ? GetModuleData : 'Current View')) || 'Current View'
                }}
            />
        </div>
    )
}
export default Template
